import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type MezzotintType = "fine-dots" | "coarse-dots" | "fine-lines" | "coarse-lines" | "stipple";

export interface MezzotintParams {
  type: MezzotintType;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const Mezzotint: Filter<MezzotintParams> = {
  id: "mezzotint",
  displayName: "Mezzotint",
  defaultParams: { type: "fine-dots" },

  apply(input: PixelBuffer, params: MezzotintParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const cellSize = params.type.startsWith("coarse") ? 4 : 2;
    const isLines = params.type.includes("lines");
    const isStipple = params.type === "stipple";

    for (let cy = 0; cy < h; cy += cellSize) {
      for (let cx = 0; cx < w; cx += cellSize) {
        let sumLum = 0, count = 0;
        const endY = Math.min(cy + cellSize, h);
        const endX = Math.min(cx + cellSize, w);

        for (let y = cy; y < endY; y++) {
          for (let x = cx; x < endX; x++) {
            const si = (y * w + x) * 4;
            sumLum += src[si]! * 0.299 + src[si + 1]! * 0.587 + src[si + 2]! * 0.114;
            count++;
          }
        }

        const avgLum = sumLum / count / 255;

        for (let y = cy; y < endY; y++) {
          for (let x = cx; x < endX; x++) {
            const o = (y * w + x) * 4;
            let val: number;

            if (isStipple) {
              val = (Math.random() > avgLum) ? 0 : 255;
            } else if (isLines) {
              const linePos = (x % cellSize) / cellSize;
              val = linePos < avgLum ? 0 : 255;
            } else {
              const dotRadius = avgLum * cellSize / 2;
              const dx = (x % cellSize) - cellSize / 2;
              const dy = (y % cellSize) - cellSize / 2;
              val = Math.sqrt(dx * dx + dy * dy) < dotRadius ? 0 : 255;
            }

            dst[o] = val; dst[o + 1] = val; dst[o + 2] = val; dst[o + 3] = src[o + 3]!;
          }
        }
      }
    }
    return out;
  },
};
